import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import LeadDrawer from "../components/LeadDrawer";
import { api } from "../lib/api";
import { fmtDate, fmtMoneyFull } from "../lib/format";
import { toast } from "sonner";
import { ChatCircleText, Flame, PaperPlaneRight, Megaphone } from "@phosphor-icons/react";

const TABS = [
  { key: "seller", label: "Seller Negotiation", icon: PaperPlaneRight, stages: ["offer_sent", "under_review"], accent: "text-amber-300", accentBg: "bg-amber-500/10 border-amber-500/40" },
  { key: "buyer", label: "Buyer Pipeline", icon: Megaphone, stages: ["hot_deals"], accent: "text-emerald-300", accentBg: "bg-emerald-500/10 border-emerald-500/40" },
];

export default function Workshop() {
  const [tab, setTab] = useState("seller");
  const [leads, setLeads] = useState([]);
  const [openId, setOpenId] = useState(null);

  const active = useMemo(() => TABS.find(t => t.key === tab) || TABS[0], [tab]);

  const load = async () => {
    try {
      const r = await api.get("/leads");
      setLeads(r.data);
    } catch { toast.error("Failed to load workshop"); }
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => leads.filter(l => active.stages.includes(l.stage)), [leads, active]);

  return (
    <AppShell header={
      <div className="border-b border-zinc-800 px-4 sm:px-6 py-4">
        <div className="overline">Integrated Workshop</div>
        <h1 className="text-lg font-semibold flex items-center gap-2"><ChatCircleText size={18} weight="bold" /> Negotiation Workshop</h1>
        <div className="mt-3 flex gap-2" data-testid="workshop-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            const on = t.key === tab;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`workshop-tab-${t.key}`}
                className={`text-[11px] mono uppercase tracking-[0.15em] px-3 py-2 rounded-sm border inline-flex items-center gap-2 transition-colors ${on ? `${t.accentBg} ${t.accent}` : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}>
                <Icon size={13} weight="bold" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>
    }>
      <div className="p-4 lg:p-6">
        {rows.length === 0 ? (
          <div className="border border-zinc-800/80 bg-[#0b0b0e] rounded-sm p-8 text-center" data-testid="workshop-empty">
            <div className="overline mb-2">{active.label}</div>
            <div className="text-sm text-zinc-500">No active deals in this view yet.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="workshop-list">
            {rows.map(l => (
              <button key={l.id} onClick={() => setOpenId(l.id)} data-testid={`workshop-card-${l.id}`}
                className="text-left border border-zinc-800 rounded-sm bg-[#0b0b0e] hover:bg-[#111114] hover:border-zinc-600 transition-colors p-3.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{l.address}</div>
                    <div className="text-[11px] mono text-zinc-500 truncate">{l.city}, {l.state} {l.zip_code}</div>
                  </div>
                  {l.stage === "hot_deals" && <Flame size={14} weight="fill" className="text-emerald-400 shrink-0" />}
                </div>
                <div className="mt-3 flex items-center justify-between text-[11px] mono">
                  <span className="text-zinc-500">Offer</span>
                  <span className="text-zinc-200">{fmtMoneyFull(l.offer_amount)}</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-[11px] mono">
                  <span className="text-zinc-500">Proj. profit</span>
                  <span className="text-emerald-400">{fmtMoneyFull(l.projected_profit)}</span>
                </div>
                <div className="mt-2 text-[10px] mono text-zinc-500">
                  {l.last_contacted_at ? `Last: ${fmtDate(l.last_contacted_at)}` : "No outbound yet"}{l.follow_up_count ? ` · FU ${l.follow_up_count}` : ""}
                </div>
                <div className="mt-2 inline-flex text-[10px] mono uppercase tracking-[0.15em] text-zinc-400">Tap to open thread + reply →</div>
              </button>
            ))}
          </div>
        )}
      </div>
      {openId && <LeadDrawer leadId={openId} onClose={() => setOpenId(null)} onUpdated={load} />}
    </AppShell>
  );
}
